import html2pdf from 'html2pdf.js';

export interface PdfExportOptions {
  margin?: number;
  filename?: string;
  pageSize?: 'a4' | 'a3' | 'letter' | 'legal';
  orientation?: 'portrait' | 'landscape';
  scale?: number;
  onStart?: () => void;
  onComplete?: () => void;
  onError?: (error: unknown) => void;
}

/**
 * Generic PDF export utility to export any HTML element as PDF
 * Adds pdf-export class to element during export for PDF-specific styling
 */
export const exportToPdf = async (element: HTMLElement, options: PdfExportOptions = {}) => {
  if (!element) {
    throw new Error('Element to export not found');
  }

  try {
    // Call onStart callback if provided
    if (options.onStart) {
      options.onStart();
    }

    // Add PDF export class for styling
    element.classList.add('pdf-export');

    // Configure PDF options
    const opt = {
      margin: options.margin ?? 10,
      filename: options.filename ?? `export-${new Date().toISOString().slice(0, 10)}.pdf`,
      image: { type: 'jpeg' as 'jpeg', quality: 0.98 },
      html2canvas: {
        scale: options.scale ?? 2,
        useCORS: true,
        logging: false,
      },
      jsPDF: {
        unit: 'mm',
        format: options.pageSize ?? 'a3',
        orientation: options.orientation ?? ('portrait' as 'portrait'),
      },
    };

    // Generate and save PDF
    await html2pdf().from(element).set(opt).save();
  } catch (error) {
    // Log error and call onError callback if provided
    console.error('Error generating PDF:', error);
    if (options.onError) {
      options.onError(error);
    }
    // Re-throw the error if no custom error handler
    if (!options.onError) {
      throw error;
    }
  } finally {
    // Always remove the PDF export class
    element.classList.remove('pdf-export');

    // Call onComplete callback if provided
    if (options.onComplete) {
      options.onComplete();
    }
  }
};
